import type {
  EvidenceLevel,
  FlagSeverity,
  MemoSectionKey,
  MoneyView,
  ProgramVersionView,
  ReadinessBlocker,
  ReviewConfidence,
  ReviewFlagView,
  ReviewResponse,
  ReviewStatus,
  ReviewView,
  ReviewedFactView,
} from "@tex/finance-shared";
import {
  EVIDENCE_LEVELS,
  FLAG_SEVERITIES,
  MEMO_SECTIONS,
  REVIEW_CONFIDENCE,
  REVIEW_STATUSES,
} from "@tex/finance-shared";
import { useQueries } from "@tanstack/react-query";
import { type FormEvent, useEffect, useState } from "react";
import { ApiError, apiGet, errorMessage } from "../lib/api";
import { formatAmount, formatDate, formatDay, humanize } from "../lib/format";
import { programKeys, usePartners } from "../lib/programs";
import {
  type FactInput,
  useAddFlag,
  useFreezeReview,
  useOpenReview,
  useReplaceFacts,
  useResolveFlag,
  useSaveMemoSection,
  useSetReviewStatus,
  useUpdateReviewDetails,
} from "../lib/reviews";
import {
  MEMO_SECTION_LABELS,
  REVIEW_STATUS_LABELS,
} from "../lib/review-labels";
import {
  Alert,
  Button,
  Card,
  Field,
  Input,
  Mono,
  Select,
  Textarea,
} from "./ui";

const MEMO_SECTION_HINTS: Record<MemoSectionKey, string> = {
  request_and_purpose:
    "What is being financed, for how much, and why now. Cite the dealer request and the borrower’s stated use.",
  business_profile:
    "Entity, time in business, industry, ownership. Say what is stated versus verified.",
  repayment_source:
    "Where the payment comes from. Do not infer capacity from data you do not have.",
  existing_obligations:
    "Known debt and leases as the borrower stated them; mark unknowns as unknown.",
  equipment_and_collateral:
    "Unit, condition, serial/title, invoice reconciliation, any dispute with the quote.",
  verification_and_fraud:
    "What was checked, against what, and anything that did not line up.",
  proposed_partner:
    "Which program this fits and which requirements are met, unmet or unknown.",
  conclusion:
    "Recommendation to package or not, in Tex’s voice. This is not a credit decision.",
};

const STATUS_HELP: Record<ReviewStatus, string> = {
  draft: "Work in progress.",
  reviewed: "Memo read through; not yet ready to package.",
  needs_information: "Waiting on the borrower or dealer.",
  specialist_review:
    "Needs a second reviewer (trade-in, title, or other specialist matter).",
  ready_to_package:
    "Every section written, no blocking flag, nothing outstanding, program chosen, confidence set.",
  not_proceeding:
    "Tex will not package this request. Requires a note. Not a credit denial.",
};

const EVIDENCE_TONE: Record<EvidenceLevel, string> = {
  stated: "bg-neutral-100 text-neutral-700",
  verified: "bg-green-100 text-green-900",
  partner_supplied: "bg-sky-100 text-sky-900",
};

const SEVERITY_TONE: Record<FlagSeverity, string> = {
  info: "bg-neutral-100 text-neutral-700",
  warning: "bg-amber-100 text-amber-900",
  blocking: "bg-red-100 text-red-900",
};

function writeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === "version_conflict")
      return "Another reviewer saved first. The review has been reloaded; re-apply your change.";
    if (err.code === "review_frozen")
      return "This version is frozen. Open a new version to amend it.";
    if (err.code === "review_not_ready")
      return `Not ready: ${err.reason ?? "blockers remain"}.`;
  }
  return errorMessage(err);
}

function blockerText(b: ReadinessBlocker): string {
  switch (b.kind) {
    case "memo_section_empty":
      return `Memo section empty: ${MEMO_SECTION_LABELS[b.section]}`;
    case "unresolved_blocking_flag":
      return `Blocking flag unresolved: ${b.code}`;
    case "open_information_request":
      return `Information request outstanding: ${b.title}`;
    case "no_target_program":
      return "No target program chosen";
    case "target_program_retired":
      return "Target program has been retired — choose another";
    case "confidence_missing":
      return "Confidence not set";
  }
}

export function ReviewWorkspace({
  caseId,
  data,
  canWrite,
}: {
  caseId: string;
  data: ReviewResponse;
  canWrite: boolean;
}) {
  const review = data.review;
  if (review === null)
    return (
      <OpenReviewCard
        caseId={caseId}
        canWrite={canWrite}
        versions={data.versions}
      />
    );
  const frozen = review.frozen_at !== null;
  const editable = canWrite && !frozen;
  return (
    <div className="space-y-6">
      <StatusCard
        caseId={caseId}
        review={review}
        editable={editable}
        canWrite={canWrite}
        versions={data.versions}
      />
      <MemoCard caseId={caseId} review={review} editable={editable} />
      <FactsCard caseId={caseId} review={review} editable={editable} />
      <FlagsCard caseId={caseId} review={review} editable={editable} />
      <DetailsCard caseId={caseId} review={review} editable={editable} />
    </div>
  );
}

function OpenReviewCard({
  caseId,
  canWrite,
  versions,
}: {
  caseId: string;
  canWrite: boolean;
  versions: ReviewResponse["versions"];
}) {
  const [key] = useState(() => crypto.randomUUID());
  const open = useOpenReview(caseId, key);
  return (
    <Card title="Review">
      <div className="space-y-3 text-sm">
        <p className="text-neutral-500">
          No review has been opened for this submission. Opening one seeds the
          eight memo sections, carries the borrower’s reported discrepancies in
          as flags, and assigns the case to you if nobody has it.
        </p>
        {versions.length > 0 && <VersionList versions={versions} />}
        {open.isError && <Alert tone="error">{writeError(open.error)}</Alert>}
        {canWrite && (
          <Button
            type="button"
            onClick={() => open.mutate(undefined)}
            disabled={open.isPending}
          >
            {open.isPending
              ? "Opening…"
              : versions.length > 0
                ? "Open a new version"
                : "Open review"}
          </Button>
        )}
      </div>
    </Card>
  );
}

function VersionList({ versions }: { versions: ReviewResponse["versions"] }) {
  return (
    <ul className="text-xs text-neutral-500">
      {versions.map((v) => (
        <li key={v.id}>
          v{v.version} · {REVIEW_STATUS_LABELS[v.status]} ·{" "}
          {v.frozen_at ? `frozen ${formatDate(v.frozen_at)}` : "open"} · opened{" "}
          {formatDate(v.created_at)}
        </li>
      ))}
    </ul>
  );
}

// ── status, readiness, freeze ──

function StatusCard({
  caseId,
  review,
  editable,
  canWrite,
  versions,
}: {
  caseId: string;
  review: ReviewView;
  editable: boolean;
  canWrite: boolean;
  versions: ReviewResponse["versions"];
}) {
  const setStatus = useSetReviewStatus(caseId);
  const [freezeKey, setFreezeKey] = useState(() => crypto.randomUUID());
  const freeze = useFreezeReview(caseId, freezeKey);
  const [openKey] = useState(() => crypto.randomUUID());
  const openNext = useOpenReview(caseId, openKey);
  const [status, setStatusDraft] = useState<ReviewStatus>(review.status);
  const [note, setNote] = useState("");
  useEffect(() => setStatusDraft(review.status), [review.status]);

  const blockers = review.readiness_blockers;
  const frozen = review.frozen_at !== null;
  const freezable =
    (review.status === "ready_to_package" && blockers.length === 0) ||
    review.status === "not_proceeding";

  const submitStatus = (e: FormEvent) => {
    e.preventDefault();
    setStatus.mutate(
      {
        expected_lock_version: review.lock_version,
        status,
        note: note.trim() || null,
      },
      { onSuccess: () => setNote("") },
    );
  };

  return (
    <Card
      title={`Review v${review.version} · ${REVIEW_STATUS_LABELS[review.status]}${frozen ? " · frozen" : ""}`}
    >
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="space-y-3">
          <h3 className="text-xs font-medium uppercase tracking-wide text-neutral-500">
            Readiness
          </h3>
          {blockers.length === 0 ? (
            <Alert tone="success">
              Nothing stands between this review and ready_to_package.
            </Alert>
          ) : (
            <ul className="space-y-1 text-sm">
              {blockers.map((b, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-amber-700">•</span>
                  <span>{blockerText(b)}</span>
                </li>
              ))}
            </ul>
          )}
          <dl className="text-xs text-neutral-500">
            <div className="flex justify-between py-0.5">
              <dt>Revision reviewed</dt>
              <dd>
                <Mono>{review.revision_number}</Mono>
              </dd>
            </div>
            <div className="flex justify-between py-0.5">
              <dt>Overall evidence</dt>
              <dd>
                {review.overall_evidence ? (
                  <EvidenceBadge level={review.overall_evidence} />
                ) : (
                  "—"
                )}
              </dd>
            </div>
            <div className="flex justify-between py-0.5">
              <dt>Lock</dt>
              <dd>
                <Mono>{review.lock_version}</Mono>
              </dd>
            </div>
            {frozen && (
              <div className="flex justify-between py-0.5">
                <dt>Frozen</dt>
                <dd>{formatDate(review.frozen_at)}</dd>
              </div>
            )}
          </dl>
          {versions.length > 1 && <VersionList versions={versions} />}
        </div>

        <div className="space-y-3">
          {frozen ? (
            <>
              <Alert tone="info">
                This version is frozen and cannot be edited. Corrections go in a
                new version, which starts from this one.
              </Alert>
              {canWrite && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => openNext.mutate(undefined)}
                  disabled={openNext.isPending}
                >
                  {openNext.isPending
                    ? "Opening…"
                    : `Open v${review.version + 1}`}
                </Button>
              )}
              {openNext.isError && (
                <Alert tone="error">{writeError(openNext.error)}</Alert>
              )}
            </>
          ) : (
            <>
              <form onSubmit={submitStatus} className="space-y-2">
                <Field label="Status" hint={STATUS_HELP[status]}>
                  <Select
                    value={status}
                    onChange={(e) =>
                      setStatusDraft(e.target.value as ReviewStatus)
                    }
                    disabled={!editable || setStatus.isPending}
                  >
                    {REVIEW_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {REVIEW_STATUS_LABELS[s]}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field
                  label={
                    status === "not_proceeding"
                      ? "Why Tex is not proceeding (required)"
                      : "Note (optional)"
                  }
                >
                  <Textarea
                    rows={2}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    required={status === "not_proceeding"}
                    disabled={!editable || setStatus.isPending}
                  />
                </Field>
                {setStatus.isError && (
                  <Alert tone="error">{writeError(setStatus.error)}</Alert>
                )}
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    variant="secondary"
                    disabled={
                      !editable ||
                      setStatus.isPending ||
                      status === review.status
                    }
                  >
                    {setStatus.isPending ? "Saving…" : "Set status"}
                  </Button>
                </div>
              </form>
              <div className="border-t border-neutral-100 pt-3">
                <p className="text-xs text-neutral-500">
                  Freezing makes v{review.version} immutable and records the
                  audit trail. Allowed once the status is ready_to_package with
                  no blockers, or not_proceeding.
                </p>
                {freeze.isError && (
                  <Alert tone="error">{writeError(freeze.error)}</Alert>
                )}
                <div className="mt-2 flex justify-end">
                  <Button
                    type="button"
                    disabled={!editable || !freezable || freeze.isPending}
                    onClick={() =>
                      freeze.mutate(
                        { expected_lock_version: review.lock_version },
                        { onSettled: () => setFreezeKey(crypto.randomUUID()) },
                      )
                    }
                  >
                    {freeze.isPending ? "Freezing…" : "Freeze review"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

// ── memo ──

function MemoCard({
  caseId,
  review,
  editable,
}: {
  caseId: string;
  review: ReviewView;
  editable: boolean;
}) {
  return (
    <Card title="Underwriting memo">
      <div className="space-y-5">
        {MEMO_SECTIONS.map((section) => (
          <MemoSection
            key={section}
            caseId={caseId}
            review={review}
            section={section}
            editable={editable}
          />
        ))}
      </div>
    </Card>
  );
}

function MemoSection({
  caseId,
  review,
  section,
  editable,
}: {
  caseId: string;
  review: ReviewView;
  section: MemoSectionKey;
  editable: boolean;
}) {
  const current = review.memo[section];
  const save = useSaveMemoSection(caseId);
  const [body, setBody] = useState(current.body);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!editing) setBody(current.body);
  }, [current.body, editing]);
  const dirty = body !== current.body;

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-medium">{MEMO_SECTION_LABELS[section]}</h3>
        <span className="text-xs text-neutral-400">
          {current.updated_at
            ? `saved ${formatDate(current.updated_at)}`
            : "empty"}
        </span>
      </div>
      {editable ? (
        <div className="mt-1 space-y-2">
          <Textarea
            rows={editing || body ? 5 : 2}
            value={body}
            placeholder={MEMO_SECTION_HINTS[section]}
            onFocus={() => setEditing(true)}
            onChange={(e) => setBody(e.target.value)}
            disabled={save.isPending}
          />
          {save.isError && <Alert tone="error">{writeError(save.error)}</Alert>}
          {(dirty || editing) && (
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setBody(current.body);
                  setEditing(false);
                }}
                disabled={save.isPending}
              >
                Discard
              </Button>
              <Button
                type="button"
                disabled={!dirty || save.isPending}
                onClick={() =>
                  save.mutate(
                    {
                      expected_lock_version: review.lock_version,
                      section,
                      body,
                    },
                    { onSuccess: () => setEditing(false) },
                  )
                }
              >
                {save.isPending ? "Saving…" : "Save section"}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <p className="mt-1 whitespace-pre-wrap text-sm text-neutral-800">
          {current.body || <span className="text-neutral-400">Not written.</span>}
        </p>
      )}
    </div>
  );
}

// ── facts ──

function EvidenceBadge({ level }: { level: EvidenceLevel }) {
  return (
    <span
      className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${EVIDENCE_TONE[level]}`}
    >
      {humanize(level)}
    </span>
  );
}

interface FactDraft {
  id?: string;
  field: string;
  value: string;
  evidence: EvidenceLevel;
  source: string;
  as_of: string;
  note: string;
}

function factValueToText(v: ReviewedFactView["value"]): string {
  if (v === null) return "";
  if (typeof v === "object")
    return `${v.currency} ${(v.amount_minor / 100).toFixed(2)}`;
  return String(v);
}

/** `USD 1234.56` → money; `true`/`false` → boolean; numeric → number; else string; blank → null. */
function factValueFromText(raw: string): FactInput["value"] {
  const t = raw.trim();
  if (t === "") return null;
  const money = /^([A-Z]{3})\s+(-?\d+)(?:\.(\d{1,2}))?$/.exec(t);
  if (money) {
    const currency = money[1] ?? "USD";
    const whole = money[2] ?? "0";
    const cents = (money[3] ?? "").padEnd(2, "0");
    const amount_minor =
      Number(whole) * 100 + (whole.startsWith("-") ? -1 : 1) * Number(cents);
    const m: MoneyView = { amount_minor, currency };
    return m;
  }
  if (t === "true") return true;
  if (t === "false") return false;
  if (/^-?\d+(\.\d+)?$/.test(t)) return Number(t);
  return t;
}

function draftsFrom(facts: ReviewedFactView[]): FactDraft[] {
  return facts.map((f) => ({
    id: f.id,
    field: f.field,
    value: factValueToText(f.value),
    evidence: f.evidence,
    source: f.source,
    as_of: f.as_of ?? "",
    note: f.note ?? "",
  }));
}

function FactsCard({
  caseId,
  review,
  editable,
}: {
  caseId: string;
  review: ReviewView;
  editable: boolean;
}) {
  const replace = useReplaceFacts(caseId);
  const [editing, setEditing] = useState(false);
  const [drafts, setDrafts] = useState<FactDraft[]>(() =>
    draftsFrom(review.facts),
  );

  const start = () => {
    setDrafts(draftsFrom(review.facts));
    replace.reset();
    setEditing(true);
  };
  const update = (i: number, patch: Partial<FactDraft>) =>
    setDrafts((prev) => prev.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const facts: FactInput[] = drafts.map((d) => ({
      ...(d.id ? { id: d.id } : {}),
      field: d.field.trim(),
      value: factValueFromText(d.value),
      evidence: d.evidence,
      source: d.source.trim(),
      as_of: d.as_of || null,
      note: d.note.trim() || null,
    }));
    replace.mutate(
      { expected_lock_version: review.lock_version, facts },
      { onSuccess: () => setEditing(false) },
    );
  };

  return (
    <Card
      title="Facts relied on"
      actions={
        editable ? (
          <Button
            type="button"
            variant="secondary"
            onClick={editing ? () => setEditing(false) : start}
            disabled={replace.isPending}
          >
            {editing ? "Cancel" : "Edit facts"}
          </Button>
        ) : undefined
      }
    >
      {editing ? (
        <form onSubmit={submit} className="space-y-3">
          <p className="text-xs text-neutral-500">
            Every fact names its source and the date it speaks as of. Marking a
            fact <em>verified</em> records you as the verifier. Money is written
            as <Mono>USD 1234.56</Mono>. Saving replaces the whole list.
          </p>
          <div className="space-y-3">
            {drafts.map((d, i) => (
              <div
                key={d.id ?? `new-${i}`}
                className="grid grid-cols-1 gap-2 rounded-md border border-[var(--border-subtle)] p-3 md:grid-cols-6"
              >
                <Field label="Field">
                  <Input
                    value={d.field}
                    onChange={(e) => update(i, { field: e.target.value })}
                    required
                    maxLength={120}
                  />
                </Field>
                <Field label="Value">
                  <Input
                    value={d.value}
                    onChange={(e) => update(i, { value: e.target.value })}
                  />
                </Field>
                <Field label="Evidence">
                  <Select
                    value={d.evidence}
                    onChange={(e) =>
                      update(i, { evidence: e.target.value as EvidenceLevel })
                    }
                  >
                    {EVIDENCE_LEVELS.map((l) => (
                      <option key={l} value={l}>
                        {humanize(l)}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Source">
                  <Input
                    value={d.source}
                    onChange={(e) => update(i, { source: e.target.value })}
                    required
                    maxLength={500}
                    placeholder="e.g. bank_statements p.2"
                  />
                </Field>
                <Field label="As of">
                  <Input
                    type="date"
                    value={d.as_of}
                    onChange={(e) => update(i, { as_of: e.target.value })}
                  />
                </Field>
                <div className="flex items-end justify-between gap-2">
                  <Field label="Note">
                    <Input
                      value={d.note}
                      onChange={(e) => update(i, { note: e.target.value })}
                    />
                  </Field>
                  <button
                    type="button"
                    className="pb-2 text-xs text-tex-red hover:underline"
                    onClick={() =>
                      setDrafts((prev) => prev.filter((_, j) => j !== i))
                    }
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="secondary"
              onClick={() =>
                setDrafts((prev) => [
                  ...prev,
                  {
                    field: "",
                    value: "",
                    evidence: "stated",
                    source: "",
                    as_of: "",
                    note: "",
                  },
                ])
              }
            >
              Add fact
            </Button>
            <Button type="submit" disabled={replace.isPending}>
              {replace.isPending ? "Saving…" : "Save facts"}
            </Button>
          </div>
          {replace.isError && (
            <Alert tone="error">{writeError(replace.error)}</Alert>
          )}
        </form>
      ) : review.facts.length === 0 ? (
        <p className="text-sm text-neutral-500">
          No facts recorded yet. Facts are what the memo cites; each carries its
          source, as-of date and evidence level.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left font-display text-[11px] font-bold uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="py-1 pr-3">Field</th>
              <th className="py-1 pr-3">Value</th>
              <th className="py-1 pr-3">Evidence</th>
              <th className="py-1 pr-3">Source</th>
              <th className="py-1 pr-3">As of</th>
              <th className="py-1">Verified</th>
            </tr>
          </thead>
          <tbody>
            {review.facts.map((f) => (
              <tr key={f.id} className="border-t border-neutral-100 align-top transition-colors hover:bg-neutral-50/60">
                <td className="py-2 pr-3 font-medium">{f.field}</td>
                <td className="py-2 pr-3">
                  <Mono>
                    {f.value !== null && typeof f.value === "object"
                      ? formatAmount(f.value)
                      : factValueToText(f.value) || "—"}
                  </Mono>
                  {f.note && (
                    <div className="text-xs text-neutral-500">{f.note}</div>
                  )}
                </td>
                <td className="py-2 pr-3">
                  <EvidenceBadge level={f.evidence} />
                </td>
                <td className="py-2 pr-3 text-neutral-700">{f.source}</td>
                <td className="py-2 pr-3">
                  {f.as_of ? formatDay(f.as_of) : "—"}
                </td>
                <td className="py-2 text-xs text-neutral-500">
                  {f.verified_at ? formatDate(f.verified_at) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

// ── flags ──

function FlagsCard({
  caseId,
  review,
  editable,
}: {
  caseId: string;
  review: ReviewView;
  editable: boolean;
}) {
  const add = useAddFlag(caseId);
  const [adding, setAdding] = useState(false);
  const [code, setCode] = useState("");
  const [severity, setSeverity] = useState<FlagSeverity>("warning");
  const [note, setNote] = useState("");
  const open = review.flags.filter((f) => f.resolved_at === null);
  const resolved = review.flags.filter((f) => f.resolved_at !== null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    add.mutate(
      {
        expected_lock_version: review.lock_version,
        code: code.trim(),
        severity,
        note: note.trim(),
      },
      {
        onSuccess: () => {
          setAdding(false);
          setCode("");
          setNote("");
        },
      },
    );
  };

  return (
    <Card
      title={`Flags${open.length ? ` · ${open.length} open` : ""}`}
      actions={
        editable ? (
          <Button
            type="button"
            variant="secondary"
            onClick={() => setAdding((v) => !v)}
          >
            {adding ? "Cancel" : "Raise flag"}
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-4">
        {adding && (
          <form
            onSubmit={submit}
            className="grid grid-cols-1 gap-2 rounded-md border border-[var(--border-subtle)] bg-neutral-50 p-3 md:grid-cols-4"
          >
            <Field label="Code" hint="snake_case">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                pattern="^[a-z][a-z0-9_:.-]{1,63}$"
                placeholder="e.g. serial_mismatch"
              />
            </Field>
            <Field label="Severity">
              <Select
                value={severity}
                onChange={(e) => setSeverity(e.target.value as FlagSeverity)}
              >
                {FLAG_SEVERITIES.map((s) => (
                  <option key={s} value={s}>
                    {humanize(s)}
                  </option>
                ))}
              </Select>
            </Field>
            <div className="md:col-span-2">
              <Field label="What is wrong">
                <Input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  required
                  maxLength={2000}
                />
              </Field>
            </div>
            {add.isError && (
              <div className="md:col-span-4">
                <Alert tone="error">{writeError(add.error)}</Alert>
              </div>
            )}
            <div className="flex justify-end md:col-span-4">
              <Button type="submit" disabled={add.isPending}>
                {add.isPending ? "Saving…" : "Raise"}
              </Button>
            </div>
          </form>
        )}
        {review.flags.length === 0 && !adding && (
          <p className="text-sm text-neutral-500">
            No flags. Contradictions and concerns go here and stay visible until
            resolved with a reason.
          </p>
        )}
        {open.length > 0 && (
          <ul className="space-y-2">
            {open.map((f) => (
              <FlagRow
                key={f.id}
                caseId={caseId}
                review={review}
                flag={f}
                editable={editable}
              />
            ))}
          </ul>
        )}
        {resolved.length > 0 && (
          <details className="text-sm">
            <summary className="cursor-pointer text-neutral-500">
              {resolved.length} resolved
            </summary>
            <ul className="mt-2 space-y-2">
              {resolved.map((f) => (
                <FlagRow
                  key={f.id}
                  caseId={caseId}
                  review={review}
                  flag={f}
                  editable={false}
                />
              ))}
            </ul>
          </details>
        )}
      </div>
    </Card>
  );
}

function FlagRow({
  caseId,
  review,
  flag,
  editable,
}: {
  caseId: string;
  review: ReviewView;
  flag: ReviewFlagView;
  editable: boolean;
}) {
  const resolve = useResolveFlag(caseId);
  const [resolving, setResolving] = useState(false);
  const [resolution, setResolution] = useState("");
  return (
    <li className="rounded-md border border-[var(--border-subtle)] p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${SEVERITY_TONE[flag.severity]}`}
        >
          {humanize(flag.severity)}
        </span>
        <Mono>{flag.code}</Mono>
        <span className="text-xs text-neutral-500">
          {flag.source === "system"
            ? "raised by the system"
            : "raised by reviewer"}{" "}
          {formatDate(flag.raised_at)}
        </span>
      </div>
      <p className="mt-1 text-neutral-800">{flag.note}</p>
      {flag.resolved_at ? (
        <p className="mt-1 text-xs text-green-800">
          Resolved {formatDate(flag.resolved_at)}: {flag.resolution}
        </p>
      ) : editable ? (
        resolving ? (
          <form
            className="mt-2 space-y-2"
            onSubmit={(e) => {
              e.preventDefault();
              resolve.mutate(
                {
                  flagId: flag.id,
                  expected_lock_version: review.lock_version,
                  resolution: resolution.trim(),
                },
                { onSuccess: () => setResolving(false) },
              );
            }}
          >
            <Field label="How it was resolved">
              <Textarea
                rows={2}
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                required
                maxLength={2000}
              />
            </Field>
            {resolve.isError && (
              <Alert tone="error">{writeError(resolve.error)}</Alert>
            )}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setResolving(false)}
              >
                Back
              </Button>
              <Button type="submit" disabled={resolve.isPending}>
                {resolve.isPending ? "Saving…" : "Resolve"}
              </Button>
            </div>
          </form>
        ) : (
          <button
            type="button"
            className="mt-1 text-xs text-tex-blue hover:underline"
            onClick={() => setResolving(true)}
          >
            Resolve with a reason
          </button>
        )
      ) : null}
    </li>
  );
}

// ── details: confidence, target program, internal notes ──

interface ProgramOption {
  id: string;
  label: string;
}

/** Active partners' programs, flattened for the target-program selector. Retired versions are refused server-side anyway. */
function useProgramOptions(): { options: ProgramOption[]; isLoading: boolean } {
  const partners = usePartners();
  const ids = partners.data?.partners.map((p) => p.id) ?? [];
  const programs = useQueries({
    queries: ids.map((id) => ({
      queryKey: programKeys.partnerPrograms(id),
      queryFn: () =>
        apiGet<{ programs: ProgramVersionView[] }>(
          `/api/v1/partners/${id}/programs`,
        ),
    })),
  });
  const byPartner = new Map(
    partners.data?.partners.map((p) => [p.id, p.display_name]) ?? [],
  );
  const options: ProgramOption[] = [];
  for (const q of programs) {
    for (const p of q.data?.programs ?? []) {
      if (p.status === "retired") continue;
      options.push({
        id: p.id,
        label: `${byPartner.get(p.partner_id) ?? p.partner_id} · ${p.title} (${p.program_code} v${p.version}, ${p.status})`,
      });
    }
  }
  return {
    options,
    isLoading: partners.isPending || programs.some((q) => q.isPending),
  };
}

function DetailsCard({
  caseId,
  review,
  editable,
}: {
  caseId: string;
  review: ReviewView;
  editable: boolean;
}) {
  const update = useUpdateReviewDetails(caseId);
  const { options, isLoading } = useProgramOptions();
  const [confidence, setConfidence] = useState<ReviewConfidence | "">(
    review.confidence ?? "",
  );
  const [reasons, setReasons] = useState(review.confidence_reasons ?? "");
  const [program, setProgram] = useState(
    review.target_program_version_id ?? "",
  );
  const [notes, setNotes] = useState(review.internal_notes ?? "");
  useEffect(() => {
    setConfidence(review.confidence ?? "");
    setReasons(review.confidence_reasons ?? "");
    setProgram(review.target_program_version_id ?? "");
    setNotes(review.internal_notes ?? "");
  }, [
    review.confidence,
    review.confidence_reasons,
    review.target_program_version_id,
    review.internal_notes,
  ]);

  const dirty =
    confidence !== (review.confidence ?? "") ||
    reasons !== (review.confidence_reasons ?? "") ||
    program !== (review.target_program_version_id ?? "") ||
    notes !== (review.internal_notes ?? "");

  const submit = (e: FormEvent) => {
    e.preventDefault();
    update.mutate({
      expected_lock_version: review.lock_version,
      confidence: confidence || null,
      confidence_reasons: reasons.trim() || null,
      target_program_version_id: program || null,
      internal_notes: notes.trim() || null,
    });
  };

  const currentProgramMissing =
    program !== "" && !options.some((o) => o.id === program);

  return (
    <Card title="Confidence, target program and notes">
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field
            label="Confidence"
            hint="Qualitative, with reasons. Not a probability of default."
          >
            <Select
              value={confidence}
              onChange={(e) =>
                setConfidence(e.target.value as ReviewConfidence | "")
              }
              disabled={!editable || update.isPending}
            >
              <option value="">Not set</option>
              {REVIEW_CONFIDENCE.map((c) => (
                <option key={c} value={c}>
                  {humanize(c)}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="Target program"
            hint={
              isLoading
                ? "Loading programs…"
                : "Which partner program this would be packaged for."
            }
          >
            <Select
              value={program}
              onChange={(e) => setProgram(e.target.value)}
              disabled={!editable || update.isPending}
            >
              <option value="">Not chosen</option>
              {currentProgramMissing && (
                <option value={program}>
                  {program} (retired or unavailable)
                </option>
              )}
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field
          label="Why that confidence"
          hint="Required whenever confidence is set."
        >
          <Textarea
            rows={3}
            value={reasons}
            onChange={(e) => setReasons(e.target.value)}
            required={confidence !== ""}
            disabled={!editable || update.isPending}
          />
        </Field>
        <Field
          label="Internal notes"
          hint="Tex-only. Never projected to the dealer, borrower or partner."
        >
          <Textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={!editable || update.isPending}
          />
        </Field>
        {update.isError && (
          <Alert tone="error">{writeError(update.error)}</Alert>
        )}
        {editable && (
          <div className="flex justify-end">
            <Button type="submit" disabled={!dirty || update.isPending}>
              {update.isPending ? "Saving…" : "Save details"}
            </Button>
          </div>
        )}
      </form>
    </Card>
  );
}
